import useAppDispatch from './useAppDispatch'
import useAppStore from './useAppStore'
import useDialog from './useDialog'
import { setValidators } from '../redux/slices/config'
import { DefaultDialogsContainerNames } from '../consts/defaultConfigValues/defaultConstants'
import getValidatorDialogsProps from '../utils/getValidatorDialogsProps'
import getConfirmOrderDialogProps from '../utils/confirmOrderDialog'
import { fireGTMEvent } from '../utils/fireGTMEvent'
import getAnalyticsItems from '../utils/getAnalyticsItems'
import { selectRuleIsSettled } from '../utils/checkoutRuleSelection'
import { CheckoutRules } from '../interfaces'

/*
  reducer: defaultSlice
  key: validations: Array<string>
  this manage to open all the dialogs for validations as a recursive way while the are pending validations, those are set on usePlaceOrder hook
*/

export const useLoadDialogValidations = () => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const { openDialog } = useDialog()
  const loadDialogs = ({ validators }: { validators: string[] }) => {
    const values = [...validators]
    const currentValidator = values.pop()
    if (currentValidator) {
      dispatch(setValidators(values))
      const validatorProps = getValidatorDialogsProps(currentValidator)
      openDialog({ ...validatorProps })
    } else {
      /*
       * Every validator has been accepted, so this is the last thing between
       * the shopper and `createOrder`. It opens whether or not any validation
       * applied, which is why every storefront in the fleet already taps twice
       * to check out.
       *
       * The detail rows are spread over the static half rather than built into
       * it. On a storefront that has not authored `config.dialogs.confirmOrder`
       * the second spread is `{}` and this dialog is byte-identical to the one
       * that shipped before COM-1047.
       *
       * ⚠️ `getValidatorDialogsProps` reads state only for the `CONFIRM_ORDER`
       * case (COM-1279): the two button labels pick dedicated tokens when the
       * storefront authored `texts.cart.confirmActions.{confirm,cancel}`, else
       * fall back to `actions.yes` / `actions.cancel`. The validator branch
       * above still calls it with no state and keeps its pure-lookup shape.
       *
       * ⚠️ `store.getState()` rather than a `useAppSelector` snapshot.
       * `loadDialogs` runs from an event handler — an accept button, or the
       * place-order queue — so the render it was built in may be several
       * dispatches old by the time it fires, and a stale total is money on
       * screen.
       */
      const state = store.getState()
      // COM-1528: `commerce-begin_checkout` fires at the shared dialog
      // boundary so both entry points (cart footer `placeOrderFlow`, summary
      // CTA `continueOrPlaceOrder`) are counted once, and a before-order
      // validator that aborts leaves no event behind. Presence facets go
      // through `selectRuleIsSettled` and `selectRulePick`, which read the
      // slice first and the persisted `sessionData.checkoutRules` behind it
      // — so a returning shopper with a saved pick that auto-settle kept
      // without copying into the slice is reported as having one.
      const cart = state.cartSlice
      fireGTMEvent('commerce-begin_checkout', {
        currency: state.defaultSlice.config?.options?.currencyPrefix ?? '',
        value: cart?.total ?? 0,
        items: cart ? getAnalyticsItems(cart) : [],
        has_delivery_date: selectRuleIsSettled(CheckoutRules.DELIVERY_DATES)(
          state,
        ),
        has_payment_method: selectRuleIsSettled(CheckoutRules.PAYMENT_METHODS)(
          state,
        ),
      })
      openDialog({
        ...getValidatorDialogsProps(
          DefaultDialogsContainerNames.CONFIRM_ORDER,
          state,
        ),
        ...getConfirmOrderDialogProps(state),
      })
    }
  }

  return loadDialogs
}

export default useLoadDialogValidations
